import type { CodeableConcept, ReferencedResourceId } from "./common";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";
import type { VisitId } from "./visit.model";

/**
 * The kinds of note we write.
 *
 * The codes live here because the handler creating a note choosing one is a
 * domain decision — each create handler names its own. How they are
 * REPRESENTED in FHIR (system, which element they land in) stays in the codec.
 */
export const NOTE_KINDS = {
  /** About the patient as a whole, written outside any visit. */
  patient: "patient-note",
  /** The one note a visit carries. */
  visit: "visit-note",
} as const;

/**
 * What a note is ABOUT, picked by the clinician from a fixed list. Separate
 * from the kind above, which records where the note was written; this is what
 * it says. Closed so the list can be filtered and reported on.
 */
export const NOTE_CATEGORIES = {
  assessment: "assessment",
  progress: "progress",
  phoneCall: "phone-call",
  planChange: "plan-change",
  teamDiscussion: "team-discussion",
  noteToFile: "note-to-file",
} as const;
export type NoteCategory = (typeof NOTE_CATEGORIES)[keyof typeof NOTE_CATEGORIES];
const CATEGORY_VALUES: readonly string[] = Object.values(NOTE_CATEGORIES);

/** Full creation context, with optional classification for a specific note kind. */
export type NoteType = {
  classification?: string;
  content: NoteContent;
};

type NoteStatus = "in-progress" | "completed" | "entered-in-error";
/** What a note can have been when it was voided — and so what reinstating it returns it to. */
type VoidedFrom = Exclude<NoteStatus, "entered-in-error">;

/** A pointer that carries its own type, because what it points at varies. */
interface TypedPointer {
  resourceType: string;
  id: ReferencedResourceId;
}

interface Finding {
  /** What was found, as a code. */
  code?: CodeableConcept;
  /** What was found, as a reference to the record that holds it. */
  reference?: TypedPointer;
  basis?: string;
}

const TRANSITIONS: Record<NoteStatus, NoteStatus[]> = {
  "in-progress": ["completed", "entered-in-error"],
  completed: ["entered-in-error"],
  // Reinstating, and only to the status recorded at the void. `sign` names
  // the status it leaves from, so these edges do not let a voided note be signed.
  "entered-in-error": ["in-progress", "completed"],
};

export interface NoteContent {
  visitId?: VisitId;
  patientId: PatientId;
  assessorId?: PractitionerId;
  date?: string;
  /** The note's title. */
  description?: string;
  category?: NoteCategory;
  summary?: string;
  note?: string;
  findings: Finding[];
}

export type NoteId = DomainId<"NoteId">;
export function asNoteId(value: string): NoteId {
  if (!value) throw new Error("NoteId cannot be empty");
  return value as NoteId;
}

export type NoteSnapshot = NoteContent &
  Omit<NoteType, "content"> & {
    id: NoteId;
    status: NoteStatus;
    /**
     * Recorded by `retract`, never supplied: a caller saying "this was signed"
     * would be forging a signature. Present only while voided.
     */
    voidedFrom?: VoidedFrom;
  };

/** Owns its content and lifecycle. Visit is only a reference held by this aggregate. */
export class Note {
  private state: NoteSnapshot;

  private constructor(props: NoteSnapshot) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Clinical note requires a patient");
    }
    if (props.visitId !== undefined && !props.visitId)
      throw new PreconditionFailedError("A visit reference cannot be empty");
    if (props.classification === NOTE_KINDS.visit && !props.visitId)
      throw new PreconditionFailedError("A visit note requires a visit");
    if (props.classification === NOTE_KINDS.patient && props.visitId)
      throw new PreconditionFailedError("A patient note cannot belong to a visit");
    if (!Object.hasOwn(TRANSITIONS, props.status)) throw new PreconditionFailedError("Unknown note status");
    if (props.voidedFrom !== undefined && props.status !== "entered-in-error")
      throw new PreconditionFailedError("Only a voided note records what it was before");
    assertCategory(props.category);
    for (const finding of props.findings) assertFinding(finding);
    this.state = structuredClone(props);
  }

  static create(type: NoteType & { id: NoteId }): Note {
    return new Note({
      ...type.content,
      classification: type.classification,
      id: asNoteId(type.id),
      status: "in-progress",
    });
  }

  static restore(snapshot: NoteSnapshot): Note {
    return new Note(snapshot);
  }

  // ── facts ──

  get classification(): string | undefined {
    return this.state.classification;
  }

  get visitId(): VisitId | undefined {
    return this.state.visitId;
  }

  get id(): NoteId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  /** Signed — a clinical-legal record. What a stage gate looks for. */
  get isSigned(): boolean {
    return this.state.status === "completed";
  }

  get isDraft(): boolean {
    return this.state.status === "in-progress";
  }

  snapshot(): NoteSnapshot {
    return structuredClone(this.state);
  }

  // ── operations ──

  /**
   * Write the note. Refused once signed — that is what signing means. A
   * `category` key present but undefined clears it; an absent key leaves it.
   */
  write(changes: { description?: string; category?: NoteCategory; summary?: string; note?: string }): void {
    this.assertDraft("write to");
    assertCategory(changes.category);
    Object.assign(this.state, changes);
    if (Object.hasOwn(changes, "category") && changes.category === undefined) delete this.state.category;
  }

  /** Record something found, or referenced, during the visit. */
  recordFinding(finding: Finding): void {
    this.assertDraft("record a finding on");
    assertFinding(finding);
    this.state.findings = [...this.state.findings, finding];
  }

  /**
   * Replace the findings outright. The note surface edits them as a SET — a
   * clinician removing one they mistyped is an ordinary edit, not a retraction —
   * whereas `recordFinding` is for a caller that observed one more thing and
   * knows nothing about the rest.
   */
  setFindings(findings: Finding[]): void {
    this.assertDraft("change the findings on");
    for (const finding of findings) assertFinding(finding);
    this.state.findings = structuredClone(findings);
  }

  /** Sign it. One way — there is no unsign. */
  sign(assessorId: PractitionerId, now: Date): void {
    this.state.status = this.next("completed", "in-progress");
    this.state.assessorId = assessorId;
    this.state.date ??= now.toISOString();
  }

  /** It should never have existed. The only thing that can happen to a signed note; `reinstate` undoes it. */
  retract(): void {
    const from = this.state.status;
    this.state.status = this.next("entered-in-error");
    this.state.voidedFrom = from as VoidedFrom;
  }

  /** Undo a void: back to exactly what it was — a draft stays a draft, a signed note stays signed. */
  reinstate(): void {
    if (this.state.status !== "entered-in-error") {
      throw new InvalidTransitionError(this.state.status, `Note:${this.state.status}→reinstate`);
    }
    const to = this.state.voidedFrom;
    if (!to) throw new PreconditionFailedError("This voided note has no record of its status before the void");
    this.state.status = this.next(to);
    delete this.state.voidedFrom;
  }

  // ── internals ──

  private next(to: NoteStatus, onlyFrom?: NoteStatus): NoteStatus {
    const from = this.state.status;
    if ((onlyFrom && from !== onlyFrom) || !TRANSITIONS[from]?.includes(to)) {
      throw new InvalidTransitionError(this.state.status, `Note:${this.state.status}→${to}`);
    }
    return to;
  }

  private assertDraft(action: string): void {
    if (!this.isDraft) {
      throw new PreconditionFailedError(
        `Cannot ${action} a note in status '${this.state.status}'; only a draft note is editable`,
      );
    }
  }
}

function assertCategory(category: string | undefined): void {
  if (category !== undefined && !CATEGORY_VALUES.includes(category)) {
    throw new PreconditionFailedError(`Unknown note category '${category}'`);
  }
}

/** A finding has to say WHAT was found — as a code, or as the record holding it. */
function assertFinding(finding: Finding): void {
  if (!finding.code && !finding.reference) {
    throw new PreconditionFailedError("A finding must say what was found, as a code or a reference");
  }
}
